"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import CreateScoopForm from "../components/scoops_components/CreateScoopForm";
import MobileCreateScoop from "../components/scoops_components/MobileCreateScoop";
import { useIsMobile } from "../hooks/use-mobile";
import MainLayout from "../components/MainLayout";
import FloatingActionButton from "../components/scoops_components/FAB";
import FilterSidebar from "../components/scoops_components/FilterSidebar";
import { postScoops, getPostings } from "../services/scoops";
import { successToast, errorToast } from "../utils/toasters";
import SocialPostCard from "../components/home_components/SocialPostCard";
import { getfilterPostings } from "../services/scoops";
import followService from "../services/follow";
import { jwtDecode } from "jwt-decode";
import CreateScoopForm2 from "../components/scoops_components/CreateScoopForm2";
import { Link } from "react-router-dom";

// Import skeleton components
import PostSkeleton from "../components/skeletons/PostSkeleton";
import FilterSidebarSkeleton from "../components/skeletons/FilterSidebarSkeleton";

function ScoopsPage() {
  const isMobile = useIsMobile();
  const [showMobileCreate, setShowMobileCreate] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const geohash = localStorage.getItem("geohash") || "9v6m";

  /***********************social media*****************/
  const [postings, setPostings] = useState([]);
  const [feedLoading, setFeedLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const isLoadingMore = useRef(false);
  const POSTS_PER_PAGE = 10;

  // Filter state
  const [activeFilters, setActiveFilters] = useState({
    categories: [],
    hoops: [],
  });

  // Loading states for different components
  const [createFormLoading, setCreateFormLoading] = useState(true);
  const [sidebarLoading, setSidebarLoading] = useState(true);

  // Helper function to safely convert errors to strings
  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message;
    if (error && error.response?.data?.message)
      return error.response.data.message;
    if (error && error.data?.message) return error.data.message;
    if (error && error.message) return error.message;
    return "An unknown error occurred";
  };

  // Helper function to safely extract posts from different response structures
  const extractPostsFromResponse = (response) => {
    if (!response) return [];

    if (Array.isArray(response.postings)) {
      return response.postings;
    }

    if (Array.isArray(response.posts)) {
      return response.posts;
    }

    if (response.posting && Array.isArray(response.posting.posts)) {
      return response.posting.posts;
    }

    return [];
  };

  // Render skeleton loaders for posts
  const renderPostSkeletons = (count = 3) => {
    return Array.from({ length: count }, (_, index) => (
      <PostSkeleton key={`post-skeleton-${index}`} />
    ));
  };

  // Last element ref callback for intersection observer
  const lastPostElementRef = useCallback(
    (node) => {
      if (feedLoading || !hasMore || isLoadingMore.current) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoadingMore.current) {
            fetchMorePosts();
          }
        },
        {
          rootMargin: "100px",
        }
      );

      if (node) observer.current.observe(node);
    },
    [feedLoading, hasMore]
  );

  // Function to fetch initial posts
  const fetchInitialPosts = async () => {
    setInitialLoading(true);
    setFeedLoading(true);
    setError(null);
    setOffset(0);
    setHasMore(true);

    const geohash = localStorage.getItem("geohash") || "9v6m";

    try {
      let response;

      if (
        activeFilters.categories.length > 0 ||
        activeFilters.hoops.length > 0
      ) {
        const requestBody = {
          hoopids: activeFilters.hoops || [],
          geohash: geohash,
          scoopids: activeFilters.categories || [],
          offset: 0,
        };

        response = await getfilterPostings(requestBody);
      } else {
        const requestBody = {
          geohash,
          offset: 0,
        };

        response = await getPostings(requestBody);
      }

      const posts = extractPostsFromResponse(response);
      setPostings(posts);

      if (posts.length > 0) {
        setOffset(posts.length);
        setHasMore(posts.length >= POSTS_PER_PAGE);
      } else {
        setOffset(0);
        setHasMore(false);
      }
    } catch (error) {
      // Only set error state for non-400 errors or if it's the initial load
      if (!error.response || error.response.status !== 400 || initialLoading) {
        const errorMessage = getErrorMessage(error);
        setError(errorMessage);
      }

      setHasMore(false);
    } finally {
      setFeedLoading(false);
      setInitialLoading(false);
    }
  };

  // Function to fetch more posts
  const fetchMorePosts = async () => {
    if (!hasMore || feedLoading || isLoadingMore.current) return;

    isLoadingMore.current = true;
    setFeedLoading(true);

    const currentOffset = offset;

    if (currentOffset <= 0) {
      setHasMore(false);
      setFeedLoading(false);
      isLoadingMore.current = false;
      return;
    }

    const geohash = localStorage.getItem("geohash") || "9v6m";

    try {
      let response;

      if (
        activeFilters.categories.length > 0 ||
        activeFilters.hoops.length > 0
      ) {
        const requestBody = {
          hoopids: activeFilters.hoops || [],
          geohash: geohash,
          scoopids: activeFilters.categories || [],
          offset: currentOffset,
        };
        response = await getfilterPostings(requestBody);
      } else {
        const requestBody = {
          geohash,
          offset: currentOffset,
        };

        response = await getPostings(requestBody);
      }

      const newPosts = extractPostsFromResponse(response);

      if (newPosts && newPosts.length > 0) {
        const existingIds = new Set(
          postings.map((post) => post.postingid || post.id || post._id)
        );
        const uniqueNewPosts = newPosts.filter(
          (post) => !existingIds.has(post.postingid || post.id || post._id)
        );

        if (uniqueNewPosts.length > 0) {
          setPostings((prevPosts) => [...prevPosts, ...uniqueNewPosts]);
          setOffset(currentOffset + uniqueNewPosts.length);
          setHasMore(uniqueNewPosts.length >= POSTS_PER_PAGE);
        } else {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      // Silently handle 400 errors when loading more (likely end of data)
      if (!error.response || error.response.status !== 400) {
        const errorMessage = getErrorMessage(error);
        setError(errorMessage);
      }

      setHasMore(false);
    } finally {
      setFeedLoading(false);
      isLoadingMore.current = false;
    }
  };

  // Refresh feed after posting or when filters change
  const refreshFeed = () => {
    setOffset(0);
    setHasMore(true);
    fetchInitialPosts();
  };

  // Handle filter changes
  const clearFilters = async () => {
    setFilterLoading(true);
    setError(null);

    setShowMobileFilter(false);

    setOffset(0);
    setHasMore(true);

    const requestBody = {
      hoopids: [],
      geohash: localStorage.getItem("geohash") || "9v6m",
      scoopids: [],
      offset: 0,
    };

    try {
      const response = await getfilterPostings(requestBody);
      const posts = extractPostsFromResponse(response);
      setPostings(posts);

      if (posts.length > 0) {
        setOffset(posts.length);
        setHasMore(posts.length >= POSTS_PER_PAGE);
      } else {
        setOffset(0);
        setHasMore(false);
      }
    } catch (error) {
      if (!error.response || error.response.status !== 400) {
        const errorMessage = getErrorMessage(error);
        setError(errorMessage);

        errorToast(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      }
      setHasMore(false);
    } finally {
      setFilterLoading(false);
    }
  };

  // Handle filter changes
  const handleApplyFilters = async (filters) => {
    setFilterLoading(true);
    setError(null);
    setActiveFilters(filters);
    setShowMobileFilter(false);

    setOffset(0);
    setHasMore(true);
    const requestBody = {
      hoopids: filters.hoops || [],
      geohash: localStorage.getItem("geohash") || "9v6m",
      scoopids: filters.categories || [],
      offset: 0,
    };

    try {
      const response = await getfilterPostings(requestBody);
      const posts = extractPostsFromResponse(response);

      setPostings(posts);

      if (posts.length > 0) {
        setOffset(posts.length);
        setHasMore(posts.length >= POSTS_PER_PAGE);
      } else {
        setOffset(0);
        setHasMore(false);
      }
      if (
        (filters.categories && filters.categories.length > 0) ||
        (filters.hoops && filters.hoops.length > 0)
      ) {
        successToast("Filters applied successfully", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      if (!error.response || error.response.status !== 400) {
        const errorMessage = getErrorMessage(error);
        setError(errorMessage);

        errorToast(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      }

      setHasMore(false);
    } finally {
      setFilterLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialPosts();

    // Simulate create form loading
    setTimeout(() => {
      setCreateFormLoading(false);
    }, 1000);

    // Simulate sidebar loading
    setTimeout(() => {
      setSidebarLoading(false);
    }, 800);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  /***********************social media end*****************/

  const handleMobilePost = async (data) => {
    try {
      setIsProcessing(true);

      if (!data.content?.trim() && (!data.images || !data.images.length)) {
        errorToast("Please enter some content or add an image", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        return;
      }

      if (!data.category) {
        errorToast("Please select a category", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
        return;
      }

      const formData = new FormData();
      formData.append("contentinfo", data.content);
      formData.append("scoopid", data.category);
      formData.append("geohash", geohash);

      const hoopids = data.hashtags.map((hashtag) => hashtag.hoopid);
      formData.append("hoopids", JSON.stringify(hoopids));

      if (data.images && data.images.length > 0) {
        data.images.forEach((image, index) => {
          if (index < 2 && image instanceof File) {
            formData.append("photopath", image);
          }
        });
      }

      await postScoops(formData);

      successToast("Scoop has been posted successfully", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });

      setShowMobileCreate(false);
      refreshFeed();
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      errorToast(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate total active filters for badge
  const totalActiveFilters =
    (activeFilters.categories?.length || 0) +
    (activeFilters.hoops?.length || 0);

  // Toggle mobile filter sidebar
  const toggleMobileFilter = () => {
    setShowMobileFilter(!showMobileFilter);
  };

  const [followingUsers, setFollowingUsers] = useState([]);

  useEffect(() => {
    async function fetchFollowingUsers() {
      const token = localStorage.getItem("access_token");
      if (!token) {
        return;
      }
      const decodedToken = jwtDecode(token);
      const followings = await followService.handleGetUserFollowings(
        decodedToken.userid
      );
      const followingUsersId = followings.data.map(
        (follow) => follow.followeduserid
      );
      setFollowingUsers(followingUsersId);
    }
    fetchFollowingUsers();
  }, []);

  if (isMobile) {
    if (showMobileCreate) {
      return (
        <MainLayout>
          <div className="min-h-screen flex flex-col">
            <MobileCreateScoop
              onClose={() => setShowMobileCreate(false)}
              onPost={handleMobilePost}
              isProcessing={isProcessing}
            />
          </div>
        </MainLayout>
      );
    }

    // For mobile, use the hamburger menu for filters
    return (
      <MainLayout onMenuClick={toggleMobileFilter} menu={true}>
        <div className="min-h-screen">
          <div style={{ padding: "1rem" }}>
            {showMobileFilter ? (
              sidebarLoading ? (
                <FilterSidebarSkeleton />
              ) : (
                <FilterSidebar
                  onClear={clearFilters}
                  onApplyFilters={handleApplyFilters}
                  onClose={() => setShowMobileFilter(false)}
                  isMobile={true}
                />
              )
            ) : null}

            {/* Filter status indicator with loading state */}
            {totalActiveFilters > 0 && (
              <div className="mb-4 bg-gray-50 rounded-lg p-3 flex justify-between items-center">
                <span className="text-sm">
                  Filtered by:{" "}
                  {activeFilters.categories.length > 0 &&
                    `${activeFilters.categories.length} categories`}
                  {activeFilters.categories.length > 0 &&
                    activeFilters.hoops.length > 0 &&
                    ", "}
                  {activeFilters.hoops.length > 0 &&
                    `${activeFilters.hoops.length} hoops`}
                </span>
                <button
                  onClick={() =>
                    handleApplyFilters({ categories: [], hoops: [] })
                  }
                  className="text-sm text-purple-700 font-medium"
                  disabled={filterLoading}
                >
                  {filterLoading ? "Clearing..." : "Clear"}
                </button>
              </div>
            )}

            {/* Filter loading indicator */}
            {filterLoading && (
              <div className="mb-4 bg-purple-50 border border-purple-100 rounded-lg p-4 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin mr-3" />
                <span className="text-purple-700">Applying filters...</span>
              </div>
            )}

            {/* Error state - only show for critical errors */}
            {error && !initialLoading && !filterLoading && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                <button
                  className="mt-2 text-sm font-medium underline"
                  onClick={refreshFeed}
                >
                  Try again
                </button>
              </div>
            )}

            {/* Create Scoop Form with Skeleton */}

            <CreateScoopForm2 refreshFeed={refreshFeed} />

            {/* Posts list with Skeleton Loading */}
            {!initialLoading && !filterLoading && postings.length > 0 && (
              <div className="space-y-4">
                <div className="flex justify-between items-center gap-2 mb-4 pt-3 px-1">
                  <h1 className="text-xl font-semibold font-fraunces truncate">
                    Scoops Around You
                  </h1>
                  <div className="flex items-center gap-2 shrink-0">
                    <Link
                      to="/groups"
                      className="text-xs px-3 py-1.5 bg-[#7B189F] text-white rounded-md font-medium shadow hover:bg-[#8F1DB8] transition-all duration-200 whitespace-nowrap"
                    >
                      Groups
                    </Link>
                  </div>
                </div>
                {postings.map((post, index) => {
                  // Ensure post has all required properties
                  const safePost = {
                    ...post,
                    avatarurl:
                      post.avatar_url ||
                      post.avatarurl ||
                      "/images/default-avatar.png",
                    username: post.username || "Anonymous",
                  };

                  return (
                    <div
                      key={post.postingid || post.id || post._id || index}
                      ref={
                        index === postings.length - 1
                          ? lastPostElementRef
                          : null
                      }
                      className="transition-all duration-300 ease-in-out"
                    >
                      {followingUsers && (
                        <SocialPostCard
                          followingUsers={followingUsers}
                          postings={postings}
                          setPostings={setPostings}
                          post={safePost}
                          username={safePost.username}
                          images={
                            post.photopath
                              ? typeof post.photopath === "string"
                                ? post.photopath.split(",")
                                : [post.photopath]
                              : []
                          }
                          isMobile={true}
                        />
                      )}
                    </div>
                  );
                })}

                {/* Loading more indicator */}
                {feedLoading && !initialLoading && !filterLoading && (
                  <div className="flex justify-center items-center py-4">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-purple-700 rounded-full animate-spin" />
                    <span className="ml-2 text-sm text-gray-500">
                      Loading more...
                    </span>
                  </div>
                )}

                {/* End of feed indicator */}
                {!hasMore &&
                  !feedLoading &&
                  !filterLoading &&
                  postings.length > 0 && (
                    <div className="text-center py-6 text-gray-500 text-sm border-t border-gray-100">
                      You've reached the end of the feed
                    </div>
                  )}
              </div>
            )}

            {/* Empty state */}
            {!initialLoading &&
              !filterLoading &&
              !feedLoading &&
              postings.length === 0 &&
              !error && (
                <div className="flex flex-col items-center justify-center py-10">
                  <p className="text-gray-500 text-center">No posts found</p>
                  {totalActiveFilters > 0 && (
                    <button
                      onClick={() =>
                        handleApplyFilters({ categories: [], hoops: [] })
                      }
                      className="mt-2 text-sm text-purple-700 font-medium"
                      disabled={filterLoading}
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}

            {/* Initial loading state with skeletons */}
            {initialLoading && !filterLoading && (
              <div className="space-y-4">
                <div className="flex justify-center items-center py-4">
                  <div className="w-8 h-8 border-4 border-gray-300 border-t-purple-700 rounded-full animate-spin" />
                  <span className="ml-2 text-gray-500">Loading posts...</span>
                </div>
                {renderPostSkeletons(3)}
              </div>
            )}
          </div>
          <FloatingActionButton
            onClick={() => {
              setShowMobileCreate(true);
            }}
            disabled={isProcessing}
          />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      rightSidebar={
        sidebarLoading ? (
          <FilterSidebarSkeleton />
        ) : (
          <FilterSidebar
            onClear={clearFilters}
            onApplyFilters={handleApplyFilters}
            isMobile={false}
            isLoading={filterLoading}
          />
        )
      }
      rs={true}
    >
      {/* Filter loading indicator for desktop */}
      {filterLoading && (
        <div className="mb-4 bg-purple-50 border border-purple-100 rounded-lg p-4 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin mr-3" />
          <span className="text-purple-700">Applying filters...</span>
        </div>
      )}

      <CreateScoopForm
        followingUsers={followingUsers}
        postings={postings}
        setPostings={setPostings}
        feedLoading={feedLoading}
        initialLoading={initialLoading}
        error={error ? String(error) : null}
        hasMore={hasMore}
        lastPostElementRef={lastPostElementRef}
        refreshFeed={refreshFeed}
      />
    </MainLayout>
  );
}

export default ScoopsPage;
